import type { MessageKey } from "../i18n/en.ts";

/** Every editor command. New features add an id here and a default binding in keymap.ts. */
export const ACTION_IDS = [
  "panView",
  "rotateView",
  "lassoSelect",
  "lassoAdd",
  "lassoSubtract",
  "drawWorkArea",
  "undo",
  "redo",
  "placeAtCenter",
  "deselect",
  "selectAll",
  "deselectAll",
  "cycleSelectTool",
  "select",
  "toggleSelect",
  "moveSelected",
  "join",
  "merge",
  "copyStyle",
  "pasteStyle",
  "toggleDecorative",
  "savePiece",
  "exitEdit",
  "enterEdit",
  "fillTool",
  "clearRegion",
  "exitFill",
  "deleteSelected",
  "deleteMenu",
  "deletePointAtCursor",
  "erase",
  "togglePoints",
  "toggleSnap",
  "fillRoadsTool",
  "cycleRoadsTool",
  "roadsClear",
  "roadsFill",
  "workArea",
  "clearWorkArea",
  "moveCanvas",
  "resizeCanvas",
  "rotateCanvas",
  "zoomMap",
  "resizeEraseBrush",
  "resizeSelectCircle",
  "resizeRoadsBrush",
  "zoomIn",
  "zoomOut",
  "zoomInMore",
  "zoomOutMore",
  "panLeft",
  "panRight",
  "panUp",
  "panDown",
  "northUp",
  "toggleDrawers",
  "toggleBasemap",
  "toggleStreetView",
  "movePoint",
  "eraseStroke",
  "roadsBoxFill",
  "roadsCircleStroke",
  "moveBoundary",
  "confirmGrab",
  "cancelGrab",
  "confirmDeleteMenu",
  "cancelDeleteMenu",
  "deleteMenuNext",
  "deleteMenuPrev",
  "deleteMenuPoints",
  "deleteMenuLines",
  "deleteMenuPointsAndLines",
] as const;

export type ActionId = (typeof ACTION_IDS)[number];

export const ACTION_LABEL: Record<ActionId, MessageKey> = {
  panView: "keymap.panView",
  rotateView: "keymap.rotateView",
  lassoSelect: "keymap.lassoSelect",
  lassoAdd: "keymap.lassoAdd",
  lassoSubtract: "keymap.lassoSubtract",
  drawWorkArea: "keymap.drawWorkArea",
  undo: "toolbar.undo",
  redo: "toolbar.redo",
  placeAtCenter: "keymap.placeAtCenter",
  deselect: "toolbar.deselect",
  selectAll: "keymap.selectAll",
  deselectAll: "keymap.deselectAll",
  cycleSelectTool: "keymap.cycleSelectTool",
  moveSelected: "keymap.moveSelected",
  join: "keymap.join",
  merge: "keymap.merge",
  copyStyle: "keymap.copyStyle",
  pasteStyle: "keymap.pasteStyle",
  toggleDecorative: "keymap.toggleDecorative",
  savePiece: "keymap.savePiece",
  exitEdit: "keymap.exitEdit",
  enterEdit: "keymap.enterEdit",
  fillTool: "keymap.fillTool",
  clearRegion: "keymap.clearRegion",
  exitFill: "keymap.exitFill",
  deleteSelected: "keymap.deleteSelected",
  deleteMenu: "keymap.deleteMenu",
  deletePointAtCursor: "keymap.deleteUnderCursor",
  erase: "keymap.erase",
  togglePoints: "keymap.showPoints",
  toggleSnap: "keymap.toggleSnap",
  fillRoadsTool: "keymap.fillRoadsTool",
  cycleRoadsTool: "keymap.cycleRoadsTool",
  roadsClear: "keymap.roadsClear",
  roadsFill: "keymap.roadsFill",
  roadsBoxFill: "keymap.roadsBoxFill",
  roadsCircleStroke: "keymap.roadsCircleStroke",
  resizeRoadsBrush: "keymap.resizeRoadsBrush",
  workArea: "keymap.workArea",
  clearWorkArea: "keymap.clearWorkArea",
  moveCanvas: "keymap.moveCanvas",
  resizeCanvas: "keymap.resizeCanvas",
  rotateCanvas: "keymap.rotateCanvas",
  select: "keymap.select",
  toggleSelect: "keymap.toggleSelect",
  zoomMap: "keymap.zoomMap",
  resizeEraseBrush: "keymap.resizeEraseBrush",
  resizeSelectCircle: "keymap.resizeSelectCircle",
  zoomIn: "keymap.zoomIn",
  zoomOut: "keymap.zoomOut",
  zoomInMore: "keymap.zoomInMore",
  zoomOutMore: "keymap.zoomOutMore",
  panLeft: "keymap.panLeft",
  panRight: "keymap.panRight",
  panUp: "keymap.panUp",
  panDown: "keymap.panDown",
  northUp: "keymap.northUp",
  toggleDrawers: "keymap.toggleDrawers",
  toggleBasemap: "keymap.toggleBasemap",
  toggleStreetView: "keymap.toggleStreetView",
  movePoint: "keymap.movePoint",
  eraseStroke: "keymap.eraseStroke",
  moveBoundary: "keymap.moveBoundary",
  confirmGrab: "keymap.confirmGrab",
  cancelGrab: "keymap.cancelGrab",
  confirmDeleteMenu: "keymap.confirmDeleteMenu",
  cancelDeleteMenu: "keymap.cancelDeleteMenu",
  deleteMenuNext: "keymap.deleteMenuNext",
  deleteMenuPrev: "keymap.deleteMenuPrev",
  deleteMenuPoints: "keymap.deleteMenuPoints",
  deleteMenuLines: "keymap.deleteMenuLines",
  deleteMenuPointsAndLines: "keymap.deleteMenuPointsAndLines",
};

export function isActionId(value: string): value is ActionId {
  return (ACTION_IDS as readonly string[]).includes(value);
}
